'use client'

import { styled } from '@mui/material/styles'
import { Lexend } from 'next/font/google'
import { CSSProperties, FC, useLayoutEffect, useRef } from 'react'

import {
    HERO_STAGE_HEIGHT,
    HERO_STAGE_WIDTH,
    type HeroAnimationConfig,
    type HeroImageLayer,
} from '@/components/Hero/heroAnimationConfig'

const lexend = Lexend({
    subsets: ['latin'],
    weight: '500',
    display: 'swap',
})

type CssVars = CSSProperties & Record<`--${string}`, string | number | undefined>

const REDUCE_MOTION = '(prefers-reduced-motion: reduce)'
const DESKTOP_MOTION = '(min-width: 900px)'
const HERO_FALLBACK_MS = 2000

function heroMotionEnabled(): boolean {
    return !window.matchMedia(REDUCE_MOTION).matches && window.matchMedia(DESKTOP_MOTION).matches
}

function decodeImage(img: HTMLImageElement): Promise<void> {
    if (typeof img.decode === 'function') return img.decode()
    if (img.complete && img.naturalWidth > 0) return Promise.resolve()
    return new Promise((resolve, reject) => {
        img.addEventListener('load', () => resolve(), { once: true })
        img.addEventListener('error', () => reject(new Error('load')), { once: true })
    })
}

const BaseImage = styled('img')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
})

const LayerImage = styled('img')({
    position: 'absolute',
    zIndex: 1,
    opacity: 0,
})

const TagImage = styled(LayerImage)({
    zIndex: 4,
})

const Connectors = styled('svg')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'visible',
    zIndex: 2,
    '& circle': {
        fill: '#82a2e9',
    },
})

const Flow = styled('path')({
    fill: 'none',
    stroke: '#82a2e9',
    strokeWidth: '3',
    strokeDasharray: '10 9',
})

const Pill = styled('div')({
    position: 'absolute',
    opacity: 1,
    zIndex: 3,
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    background: '#eceef2',
    color: '#5b6270',
    fontWeight: 500,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    height: 'var(--h)',
    padding: '0 var(--pad-r) 0 var(--pad-l)',
    gap: 'var(--gap)',
    fontSize: 'var(--font)',
    lineHeight: 'var(--lh)',
    borderRadius: 'var(--radius)',
    left: 'var(--cx)',
    top: 'var(--cy)',
})

const Dot = styled('span')({
    width: 'var(--dot)',
    height: 'var(--dot)',
    borderRadius: '50%',
    background: '#8a93a3',
    flex: 'none',
})

const Placeholder = styled('div')({
    position: 'absolute',
    opacity: 1,
    zIndex: 3,
    left: 'var(--x)',
    top: 'var(--y)',
    width: 'var(--w)',
    height: 'var(--h)',
    borderRadius: 'var(--radius)',
    background: '#e3e5e9',
})

const entranceTiming = {
    animationDuration: 'var(--dur)',
    animationTimingFunction: 'var(--ease)',
    animationDelay: 'var(--d)',
    animationFillMode: 'forwards',
}

const Stage = styled('div')({
    position: 'absolute',
    inset: 0,
    containerType: 'size',
    // `flow` / `pulse` run in the default start state. The play copies use a
    // different name so that sequence starts at offset 0 and does not consume
    // flows_until_ms or shift the dot phase.
    '@keyframes flow': {
        to: { strokeDashoffset: '-19' },
    },
    '@keyframes flowPlay': {
        to: { strokeDashoffset: '-19' },
    },
    '@keyframes pulse': {
        '50%': { opacity: 0.35, transform: 'scale(0.8)' },
    },
    '@keyframes pulsePlay': {
        '50%': { opacity: 0.35, transform: 'scale(0.8)' },
    },
    '@keyframes skelPulse': {
        '50%': { backgroundColor: '#eef0f3' },
    },
    '@keyframes heroOut': {
        to: { opacity: 0 },
    },
    '@keyframes fadeScale': {
        from: { opacity: 0, transform: 'scale(var(--from))' },
        to: { opacity: 1, transform: 'none' },
    },
    '@keyframes slideY': {
        from: { opacity: 0, transform: 'translateY(var(--from))' },
        to: { opacity: 1, transform: 'none' },
    },
    '@keyframes slideX': {
        from: { opacity: 0, transform: 'translateX(var(--from))' },
        to: { opacity: 1, transform: 'none' },
    },
    '@keyframes growY': {
        to: { transform: 'none' },
    },
    // The start frame is the default, so the finished illustration is not visible
    // before HeroAnimation begins playback.
    '& [data-hero-dot]': {
        animationName: 'pulse',
        animationDuration: '1s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
    },
    '& [data-hero-placeholder]': {
        animationName: 'skelPulse',
        animationDuration: '1s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
    },
    '& [data-hero-flow]': {
        animationName: 'flow',
        animationDuration: '550ms',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
    },
    '&[data-hero-phase="play"] [data-effect="fade-scale"]': {
        ...entranceTiming,
        animationName: 'fadeScale',
    },
    '&[data-hero-phase="play"] [data-effect="slide-y"]': {
        ...entranceTiming,
        transform: 'translateY(var(--from))',
        animationName: 'slideY',
    },
    '&[data-hero-phase="play"] [data-effect="slide-x"]': {
        ...entranceTiming,
        transform: 'translateX(var(--from))',
        animationName: 'slideX',
    },
    '&[data-hero-phase="play"] [data-effect="grow-y"]': {
        ...entranceTiming,
        opacity: 1,
        transform: 'scaleY(0)',
        transformOrigin: '50% 100%',
        animationName: 'growY',
    },
    '&[data-hero-phase="play"] [data-hero-dot]': {
        animationName: 'pulsePlay',
        animationDuration: '1s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
    },
    '&[data-hero-phase="play"] [data-hero-pill]': {
        ...entranceTiming,
        animationName: 'heroOut',
    },
    '&[data-hero-phase="play"] [data-hero-placeholder]': {
        animationName: 'skelPulse, heroOut',
        animationDuration: '1s, var(--dur)',
        animationTimingFunction: 'ease-in-out, var(--ease)',
        animationDelay: '0s, var(--d)',
        animationIterationCount: 'infinite, 1',
        animationFillMode: 'none, forwards',
    },
    '&[data-hero-phase="play"] [data-hero-flow]': {
        animationName: 'flowPlay',
        animationDuration: '550ms',
        animationTimingFunction: 'linear',
        animationIterationCount: 'var(--cycles)',
        animationFillMode: 'forwards',
    },
    '&[data-hero-phase="final"] [data-effect]': {
        opacity: 1,
        transform: 'none',
        animation: 'none',
    },
    '&[data-hero-phase="final"] [data-hero-pill], &[data-hero-phase="final"] [data-hero-placeholder]':
        {
            opacity: 0,
            animation: 'none',
        },
    '&[data-hero-phase="final"] [data-hero-flow], &[data-hero-phase="final"] [data-hero-dot]': {
        animation: 'none',
    },
    '@media (prefers-reduced-motion: reduce), (max-width: 899px)': {
        '& [data-effect]': {
            opacity: '1 !important',
            transform: 'none !important',
            animation: 'none !important',
        },
        '& [data-hero-pill], & [data-hero-placeholder]': {
            opacity: '0 !important',
            animation: 'none !important',
        },
        '& [data-hero-flow], & [data-hero-dot]': {
            animation: 'none !important',
        },
    },
})

function cqh(stagePx: number): string {
    return `${(stagePx / HERO_STAGE_HEIGHT) * 100}cqh`
}

function layerVars(layer: HeroImageLayer): CssVars {
    const from =
        layer.from == null
            ? undefined
            : layer.effect === 'fade-scale'
              ? String(layer.from)
              : cqh(layer.from)
    return {
        left: `${(layer.x / HERO_STAGE_WIDTH) * 100}%`,
        top: `${(layer.y / HERO_STAGE_HEIGHT) * 100}%`,
        width: `${(layer.w / HERO_STAGE_WIDTH) * 100}%`,
        height: `${(layer.h / HERO_STAGE_HEIGHT) * 100}%`,
        '--d': `${layer.delayMs}ms`,
        '--dur': `${layer.durationMs}ms`,
        '--ease': layer.easing,
        '--from': from,
    }
}

const HeroAnimation: FC<{
    config: HeroAnimationConfig
    alt: string
}> = ({ config, alt }) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const src = (file: string) => `${config.srcDir}/${file}`

    useLayoutEffect(() => {
        const root = rootRef.current
        if (!root || !heroMotionEnabled()) return

        let settled = false
        const showFinal = () => {
            if (settled || !root.isConnected) return
            settled = true
            root.dataset.heroPhase = 'final'
        }
        const timer = window.setTimeout(showFinal, HERO_FALLBACK_MS)
        const images: HTMLImageElement[] = []
        root.querySelectorAll('img').forEach((img) => images.push(img))
        Promise.all(images.map((img) => decodeImage(img))).then(
            () => {
                if (settled || !root.isConnected || !heroMotionEnabled()) return
                settled = true
                window.clearTimeout(timer)
                root.dataset.heroPhase = 'play'
            },
            () => {
                window.clearTimeout(timer)
                showFinal()
            },
        )

        return () => {
            settled = true
            window.clearTimeout(timer)
            delete root.dataset.heroPhase
        }
    }, [])

    const imageLayers = config.layers.filter((layer) => layer.stack === 'image')
    const tagLayers = config.layers.filter((layer) => layer.stack === 'tag')

    return (
        <Stage ref={rootRef} data-hero-animation="">
            <BaseImage
                src={src('base.webp')}
                alt={alt}
                width={3840}
                height={1520}
                fetchPriority="high"
                decoding="async"
            />
            {imageLayers.map((layer) => (
                <LayerImage
                    key={layer.id}
                    data-effect={layer.effect}
                    style={layerVars(layer)}
                    src={src(layer.file)}
                    alt=""
                    aria-hidden="true"
                    width={layer.w}
                    height={layer.h}
                    decoding="async"
                />
            ))}
            <Connectors viewBox="0 0 3840 1520" aria-hidden="true">
                {config.connectors.map((connector) => (
                    <Flow
                        key={connector.id}
                        data-hero-flow=""
                        d={connector.d}
                        style={{ '--cycles': connector.cycles } as CssVars}
                    />
                ))}
                {config.connectors.map((connector) =>
                    connector.x !== undefined && connector.y !== undefined ? (
                        <circle
                            key={`${connector.id}-dot`}
                            cx={connector.x}
                            cy={connector.y}
                            r={6.5}
                        />
                    ) : null,
                )}
            </Connectors>
            {config.pills.map((pill) => (
                <Pill
                    key={pill.id}
                    data-hero-pill=""
                    className={lexend.className}
                    style={
                        {
                            '--cx': cqh(pill.cx),
                            '--cy': cqh(pill.cy),
                            '--h': cqh(pill.height),
                            '--font': cqh(pill.fontSize),
                            '--dot': cqh(pill.dot),
                            '--pad-l': cqh(pill.padL),
                            '--pad-r': cqh(pill.padR),
                            '--gap': cqh(pill.gap),
                            '--radius': cqh(pill.radius),
                            '--lh': pill.height === 51 ? cqh(pill.height) : 'normal',
                            '--d': `${pill.delayMs}ms`,
                            '--dur': `${pill.durationMs}ms`,
                            '--ease': pill.easing,
                        } as CssVars
                    }
                    aria-hidden="true"
                >
                    <Dot data-hero-dot="" />
                    {pill.label}
                </Pill>
            ))}
            {config.placeholders.map((placeholder) => (
                <Placeholder
                    key={placeholder.id}
                    data-hero-placeholder=""
                    style={
                        {
                            '--x': cqh(placeholder.x),
                            '--y': cqh(placeholder.y),
                            '--w': cqh(placeholder.w),
                            '--h': cqh(placeholder.h),
                            '--radius': cqh(placeholder.radius),
                            '--d': `${placeholder.delayMs}ms`,
                            '--dur': `${placeholder.durationMs}ms`,
                            '--ease': placeholder.easing,
                        } as CssVars
                    }
                    aria-hidden="true"
                />
            ))}
            {tagLayers.map((layer) => (
                <TagImage
                    key={layer.id}
                    data-effect={layer.effect}
                    style={layerVars(layer)}
                    src={src(layer.file)}
                    alt=""
                    aria-hidden="true"
                    width={layer.w}
                    height={layer.h}
                    decoding="async"
                />
            ))}
        </Stage>
    )
}

export default HeroAnimation
